import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import classNames from "classnames";
import { useSetAtom } from "jotai";
import {
  setConfirmationModalAtom,
  setInfoModalAtom,
  setModalCloseAtom,
} from "atoms/modalAtom/modalAtom";

import { BoldText } from "components/BoldText/BoldText";
import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { RegularText } from "components/RegularText/RegularText";
import Radio from "components/Radio/Radio";
import { NamedInput } from "components/NamedInput/NamedInput";

import {
  useCreateChequePrintTemplate,
  useDeleteChequePrintTemplate,
  useUpdateChequePrintTemplate,
} from "../api";
import {
  ChequePrintTemplate,
  ChequePrintTemplateKind,
  flagsFromKind,
  templateKindFromFlags,
} from "../types";
import { ChequePrintTemplatesFooter } from "./ChequePrintTemplatesFooter";

import styles from "../styles.module.scss";

export type ChequeTemplateFormValues = {
  name: string;
  kind: ChequePrintTemplateKind;
  value: string;
};

type Props = {
  template: ChequePrintTemplate | null;
  isNew: boolean;
  onCreated: (t: ChequePrintTemplate) => void;
  onDeleted: () => void;
};

const KIND_OPTIONS: { value: ChequePrintTemplateKind; label: string }[] = [
  { value: "slip", label: "Слип" },
  { value: "nakladnaya", label: "Накладная" },
  { value: "other", label: "Прочее" },
];

function apiErrorMessage(e: unknown, fallback: string): string {
  if (e && typeof e === "object" && "response" in e) {
    const data = (e as { response?: { data?: { message?: string } } }).response
      ?.data;
    if (data?.message && typeof data.message === "string") return data.message;
  }
  if (e && typeof e === "object" && "message" in e) {
    return String((e as { message?: string }).message);
  }
  return fallback;
}

export function ChequePrintTemplateEditor({
  template,
  isNew,
  onCreated,
  onDeleted,
}: Props): JSX.Element {
  const setInfoModal = useSetAtom(setInfoModalAtom);
  const setConfirmation = useSetAtom(setConfirmationModalAtom);
  const closeModal = useSetAtom(setModalCloseAtom);

  const { mutateAsync: create, isLoading: creating } =
    useCreateChequePrintTemplate();
  const { mutateAsync: update, isLoading: updating } =
    useUpdateChequePrintTemplate();
  const { mutateAsync: remove, isLoading: deleting } =
    useDeleteChequePrintTemplate();

  const busy = creating || updating || deleting;

  const { control, handleSubmit, reset, watch } =
    useForm<ChequeTemplateFormValues>({
      defaultValues: {
        name: "",
        kind: "other",
        value: "",
      },
    });

  useEffect(() => {
    if (isNew || !template) {
      reset({
        name: "",
        kind: "other",
        value: "",
      });
      return;
    }
    reset({
      name: template.name ?? "",
      kind: templateKindFromFlags(template),
      value: template.value ?? "",
    });
  }, [template, isNew, reset]);

  const title = isNew
    ? "Новый шаблон"
    : watch("name") || template?.name || "Шаблон";

  const handleSave = async (data: ChequeTemplateFormValues) => {
    const name = data.name?.trim();
    if (!name) {
      setInfoModal({ title: "Укажите название" });
      return;
    }
    const flags = flagsFromKind(data.kind);
    const valuePayload = data.value.length ? data.value : null;

    try {
      if (isNew) {
        const created = await create({
          name,
          value: valuePayload,
          ...flags,
        });
        onCreated(created);
        return;
      }
      if (template) {
        await update({
          printtemplateid: template.printtemplateid,
          name,
          value: valuePayload,
          ...flags,
        });
      }
    } catch (e: unknown) {
      setInfoModal({ title: apiErrorMessage(e, "Ошибка сохранения") });
    }
  };

  const handleDelete = () => {
    if (!template?.printtemplateid) return;
    setConfirmation({
      title: "Удалить этот шаблон?",
      onConfirm: async () => {
        try {
          await remove(template.printtemplateid);
          closeModal();
          onDeleted();
        } catch (e: unknown) {
          setInfoModal({ title: apiErrorMessage(e, "Не удалось удалить") });
        }
      },
    });
  };

  return (
    <BaseLayout
      className={classNames("ml-2 mt-3 mb-3", styles.editorLayout)}
      header={<BoldText className={styles.editorTitle}>{title}</BoldText>}
      footer={
        <ChequePrintTemplatesFooter
          showDelete={!isNew && !!template}
          onSave={() => handleSubmit(handleSave)()}
          onDelete={handleDelete}
          disabled={busy}
        />
      }
    >
      <div className={styles.formBody}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <NamedInput
              {...field}
              label="Название"
              placeholder="Название шаблона"
              type="text"
              isRequired
              isLabelRegular
              className={styles.nameInput}
              classNameContainer={styles.nameField}
            />
          )}
        />
        <VerticalSpace size={16} />
        <RegularText>Тип шаблона:</RegularText>
        <VerticalSpace size={8} />
        <div className={styles.radioColumn}>
          {KIND_OPTIONS.map((opt) => (
            <Controller
              key={opt.value}
              name="kind"
              control={control}
              render={({ field }) => (
                <Radio
                  id={`cheque-kind-${opt.value}`}
                  name="chequePrintTemplateKind"
                  text={opt.label}
                  value={opt.value}
                  checked={field.value === opt.value}
                  onChange={() => field.onChange(opt.value)}
                />
              )}
            />
          ))}
        </div>
        <VerticalSpace size={16} />
        <RegularText>Наполнение:</RegularText>
        <VerticalSpace size={8} />
        <Controller
          name="value"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              className={styles.contentTextarea}
              spellCheck={false}
              placeholder="Текст шаблона (сохраняются пробелы и переносы строк)"
              rows={18}
            />
          )}
        />
      </div>
    </BaseLayout>
  );
}
